import { useEffect, useRef, useState } from 'react';
import { FieldValues, Path, UseFormReturn } from 'react-hook-form';

interface UseWizardStepsProps<TFormValues extends FieldValues> {
  methods: UseFormReturn<TFormValues>;
  stepFields: Path<TFormValues>[][];
  validateFieldsOnStepEnter?: (step: number, values: TFormValues) => Path<TFormValues>[];
}

export const useWizardSteps = <TFormValues extends FieldValues>({
  methods,
  stepFields,
  validateFieldsOnStepEnter,
}: UseWizardStepsProps<TFormValues>) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [navigationInProgress, setNavigationInProgress] = useState(false);
  const navigationInProgressRef = useRef(false);
  const pendingEntryValidationRef = useRef<number | null>(null);
  const lastStep = stepFields.length - 1;

  useEffect(() => {
    if (pendingEntryValidationRef.current !== currentStep) return;
    pendingEntryValidationRef.current = null;

    const fields = validateFieldsOnStepEnter?.(currentStep, methods.getValues()) ?? [];
    if (fields.length > 0) {
      // Run after the destination step renders so any cross-step error is
      // attached to a visible, corrective field instead of trapping the user
      // on the previous step.
      void methods.trigger(fields);
    }
  }, [currentStep, methods, validateFieldsOnStepEnter]);

  const goNext = async () => {
    if (navigationInProgressRef.current) return;

    navigationInProgressRef.current = true;
    setNavigationInProgress(true);

    try {
      const isValid = await methods.trigger(stepFields[currentStep]);
      if (isValid) {
        const nextStep = Math.min(currentStep + 1, lastStep);
        if (nextStep !== currentStep) {
          pendingEntryValidationRef.current = nextStep;
          setCurrentStep(nextStep);
        }
      }
    } finally {
      navigationInProgressRef.current = false;
      setNavigationInProgress(false);
    }
  };

  const goBack = () => {
    setCurrentStep((step) => Math.max(step - 1, 0));
  };

  return {
    currentStep,
    goBack,
    goNext,
    isLastStep: currentStep === lastStep,
    navigationInProgress,
    setCurrentStep,
  };
};
